import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import type {
  Idea,
  Position,
  Post,
  Project,
  PublishAttempt,
  Report,
  ResearchBrief,
  StateSetter,
} from '../types'

// StudioStateContext owns the durable studio entities returned by
// /api/studio/bootstrap that App.jsx previously stored as local useState.
//
// Wave 7 Phase A — extracts posts, ideas, research briefs, projects,
// positions, reports, and publish attempts. Truth, authority, and proof
// state stay in App.jsx for now and will move into their own contexts in
// later phases.
//
// Wave 15 — promoted to TypeScript with explicit per-slot types.

export interface StudioStateSnapshot {
  posts: Post[]
  archivedPosts: Post[]
  // Perf 2026-06-10 — archived posts load OFF the blocking bootstrap; the
  // count arrives in the bootstrap payload so shell/dashboard/settings totals
  // are correct immediately, while archivedPosts fills in via a deferred fetch.
  archivedPostsCount: number
  ideas: Idea[]
  archivedIdeas: Idea[]
  researchBriefs: ResearchBrief[]
  projects: Project[]
  positions: Position[]
  reports: Report[]
  publishAttempts: PublishAttempt[]
}

export interface StudioStateContextValue extends StudioStateSnapshot {
  setPosts: StateSetter<Post[]>
  setArchivedPosts: StateSetter<Post[]>
  setArchivedPostsCount: StateSetter<number>
  setIdeas: StateSetter<Idea[]>
  setArchivedIdeas: StateSetter<Idea[]>
  setResearchBriefs: StateSetter<ResearchBrief[]>
  setProjects: StateSetter<Project[]>
  setPositions: StateSetter<Position[]>
  setReports: StateSetter<Report[]>
  setPublishAttempts: StateSetter<PublishAttempt[]>
}

const DEFAULT_STATE: StudioStateSnapshot = Object.freeze({
  posts: [],
  archivedPosts: [],
  archivedPostsCount: 0,
  ideas: [],
  archivedIdeas: [],
  researchBriefs: [],
  projects: [],
  positions: [],
  reports: [],
  publishAttempts: [],
}) as StudioStateSnapshot

const noop = (): void => {}

const StudioStateContext = createContext<StudioStateContextValue>({
  ...DEFAULT_STATE,
  setPosts: noop,
  setArchivedPosts: noop,
  setArchivedPostsCount: noop,
  setIdeas: noop,
  setArchivedIdeas: noop,
  setResearchBriefs: noop,
  setProjects: noop,
  setPositions: noop,
  setReports: noop,
  setPublishAttempts: noop,
})

export interface StudioStateProviderProps {
  children?: ReactNode
  initialState?: Partial<StudioStateSnapshot>
}

export function StudioStateProvider({
  children,
  initialState = {},
}: StudioStateProviderProps) {
  const [posts, setPosts] = useState<Post[]>(initialState.posts || [])
  const [archivedPosts, setArchivedPosts] = useState<Post[]>(
    initialState.archivedPosts || []
  )
  const [archivedPostsCount, setArchivedPostsCount] = useState<number>(
    initialState.archivedPostsCount || 0
  )
  const [ideas, setIdeas] = useState<Idea[]>(initialState.ideas || [])
  const [archivedIdeas, setArchivedIdeas] = useState<Idea[]>(
    initialState.archivedIdeas || []
  )
  const [researchBriefs, setResearchBriefs] = useState<ResearchBrief[]>(
    initialState.researchBriefs || []
  )
  const [projects, setProjects] = useState<Project[]>(
    initialState.projects || []
  )
  const [positions, setPositions] = useState<Position[]>(
    initialState.positions || []
  )
  const [reports, setReports] = useState<Report[]>(initialState.reports || [])
  const [publishAttempts, setPublishAttempts] = useState<PublishAttempt[]>(
    initialState.publishAttempts || []
  )

  const value = useMemo<StudioStateContextValue>(
    () => ({
      posts,
      setPosts,
      archivedPosts,
      setArchivedPosts,
      archivedPostsCount,
      setArchivedPostsCount,
      ideas,
      setIdeas,
      archivedIdeas,
      setArchivedIdeas,
      researchBriefs,
      setResearchBriefs,
      projects,
      setProjects,
      positions,
      setPositions,
      reports,
      setReports,
      publishAttempts,
      setPublishAttempts,
    }),
    [
      posts,
      archivedPosts,
      archivedPostsCount,
      ideas,
      archivedIdeas,
      researchBriefs,
      projects,
      positions,
      reports,
      publishAttempts,
    ]
  )

  return (
    <StudioStateContext.Provider value={value}>
      {children}
    </StudioStateContext.Provider>
  )
}

export function useStudioState(): StudioStateContextValue {
  return useContext(StudioStateContext)
}

export { DEFAULT_STATE, StudioStateContext }
